import { Injectable } from '@angular/core';
import { ExportSelection, SelectionAction } from 'gung-list/public-api';
import { AuthService, DocumentTemplatesService, NavbarConfigService, Product, ProductConcept, ProductExportActionConfigService, ProductExportService, ProductService } from 'gung-standard';
import { Observable, filter, of, tap } from 'rxjs';
import { first, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ExportDragProductService } from 'gung-dynamic-pdf';

@Injectable({
  providedIn: 'root'
})
export class TyloProductExportActionConfigService extends ProductExportActionConfigService {

  constructor(
    protected productExportService: ProductExportService,
    protected authService: AuthService,
    protected modalService: NgbModal,
    protected router: Router,
    protected documentTemplatesService: DocumentTemplatesService,
    protected navbarConfigService: NavbarConfigService,
    protected exportDragProductService: ExportDragProductService,
    protected productService: ProductService
  ) {
    super(
      productExportService,
      authService,
      modalService,
      router,
      documentTemplatesService,
      navbarConfigService,
      exportDragProductService,
    );
  }

  isHelo = environment.projectName == 'helo';

  getSelectionActions(): Observable<SelectionAction<Product>[]> {
    if (this.isHelo) {
      return this.getSelectionActionsHelo();
    }
    return this.authService.getRoles().pipe(
      first(),
      map(roles => {
        const userActions: SelectionAction<Product>[] = [
          {
            label: 'PRODUCT_EXPORT_XLSX',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportExcel(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_IMG',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportImg(itemList).pipe(first()).subscribe();
              return of();
            }
          }
        ];
        if (roles.length === 1 && roles.includes('USER')) {
          return userActions;
        }

        const actions: SelectionAction<Product>[] = [
          {
            label: 'PRODUCT_EXPORT_XLSX',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportExcel(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_XLSX_IMAGES_DOCUMENTS',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              Object.values(selection.selectedItems).forEach(item => {
                if (item.productType === 'concept') {
                  itemList.push(
                    ...(item as ProductConcept).subProductIds.map(subId => {
                      return {
                        id: subId,
                        name: '',
                        description: '',
                        imageUrlBig: '',
                        imageUrlSmall: '',
                        extra: {}
                      };
                    })
                  );
                }
              });
              this.productExportService.exportExcel(itemList, 'IMAGE_AND_DOCUMENTS').pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_CSV',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportCsv(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_LINESHEET',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportLinesheet(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_PRICAT',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportPricat(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_IMG',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportImg(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_PRODUCT_INFO',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportProductInfo(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_PRODUCT_STOCK',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportProductStock(itemList).pipe(first()).subscribe();
              return of();
            }
          }
        ];
        return actions;
      })
    );
  }

  getSelectionActionsHelo(): Observable<SelectionAction<Product>[]> {
    return this.authService.getRoles().pipe(
      first(),
      map(roles => {
        const userActions: SelectionAction<Product>[] = [
          {
            label: 'PRODUCT_EXPORT_XLSX',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = this.expandConceptSubArticleIds(Object.values(selection.selectedItems));
              this.productService.getProductsByIds(itemList).pipe(first()).subscribe(e => {
                this.productExportService.exportExcel(e).pipe(first()).subscribe();
              });
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_IMG',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportImg(itemList).pipe(first()).subscribe();
              return of();
            }
          }
        ];
        if (roles.length === 1 && roles.includes('USER')) {
          return userActions;
        }

        const actions: SelectionAction<Product>[] = [
          {
            label: 'PRODUCT_EXPORT_XLSX',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = this.expandConceptSubArticleIds(Object.values(selection.selectedItems));
              this.productService.getProductsByIds(itemList).pipe(first()).subscribe(e => {
                this.productExportService.exportExcel(e).pipe(first()).subscribe();
              });
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_XLSX_IMAGES_DOCUMENTS',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              Object.values(selection.selectedItems).forEach(item => {
                if (item.productType === 'concept') {
                  itemList.push(
                    ...(item as ProductConcept).subProductIds.map(subId => {
                      return {
                        id: subId,
                        name: '',
                        description: '',
                        imageUrlBig: '',
                        imageUrlSmall: '',
                        extra: {}
                      };
                    })
                  );
                }
              });
              this.productExportService.exportExcel(itemList, 'IMAGE_AND_DOCUMENTS').pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_CSV',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportCsv(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_LINESHEET',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportLinesheet(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_PRICAT',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportPricat(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_IMG',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportImg(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_PRODUCT_INFO',
            performAction: (selection: ExportSelection<Product>) => {
              let itemList = Object.values(selection.selectedItems);
              this.productExportService.exportProductInfo(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_PRODUCT_STOCK',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportProductStock(itemList).pipe(first()).subscribe();
              return of();
            }
          }
        ];
        return actions;
      })
    );
  }
  
  private expandConceptSubArticleIds(products): string[] {
    const productIds = products.map(p => [p.id, ...p.subProductIds]).reduce((acc, curr) => {
      return acc.concat(curr);
    });
    return productIds;
  }
}
